import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit, Trash2, Tag, X, ChevronLeft, AlertCircle } from 'lucide-react'
import { api } from '../../../lib/api'
import { useStore } from '../../../contexts/StoreContext'
import { useStoreValidation } from '../../../hooks/useStoreValidation'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

interface Category {
  id: string
  name: string
  slug: string
  store_id: string
  created_at: string
}

const StoreCategories = () => {
  const navigate = useNavigate()
  const { activeStore } = useStore()
  const { isStoreApproved, requireApprovedStore, getStoreStatusMessage } = useStoreValidation()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [formData, setFormData] = useState({
    name: ''
  })

  useEffect(() => {
    if (activeStore) {
      loadCategories()
    } else {
      // Si no hay activeStore, intentar obtenerlo de la URL
      const storeId = window.location.pathname.match(/\/store\/([^/]+)\/categories/)?.[1]
      if (storeId) {
        // Cargar categorías usando el storeId de la URL
        loadCategoriesByStoreId(storeId)
      }
    }
  }, [activeStore])

  const loadCategoriesByStoreId = async (storeId: string) => {
    const cacheKey = `store_categories_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedCategories = getCacheStale<Category[]>(cacheKey)
    if (cachedCategories && cachedCategories.length > 0) {
      setCategories(cachedCategories)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/products/store/${storeId}/categories`)
      const categoriesData = response.data.data || []
      setCategories(categoriesData)
      
      // Guardar en cache (1 hora de TTL)
      setCache(cacheKey, categoriesData, 60 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading categories:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedCategories) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    if (!activeStore) return

    const cacheKey = `store_categories_${activeStore.id}`
    
    // Intentar cargar desde cache primero
    const cachedCategories = getCacheStale<Category[]>(cacheKey)
    if (cachedCategories && cachedCategories.length > 0) {
      setCategories(cachedCategories)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get(`/products/store/${activeStore.id}/categories`)
      const categoriesData = response.data.data || []
      setCategories(categoriesData)
      
      // Guardar en cache (1 hora de TTL - las categorías cambian poco)
      setCache(cacheKey, categoriesData, 60 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading categories:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedCategories) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({ name: '' })
    setEditingCategory(null)
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  }

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!requireApprovedStore('crear categorías')) return

    try {
      await api.post(`/products/my-store/categories`, {
        name: formData.name,
        slug: generateSlug(formData.name)
      })

      toast.success('Categoría creada exitosamente')
      setShowCreateModal(false)
      resetForm()
      loadCategories()
    } catch (error: any) {
      console.error('Error creating category:', error)
      if (error.response?.status === 404) {
        toast.error('No se encontró el negocio. Por favor, verifica que tu negocio esté aprobado.')
      } else {
        toast.error(error.response?.data?.message || 'Error al crear la categoría')
      }
    }
  }

  const handleEditCategory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCategory || !activeStore) return

    try {
      await api.put(`/products/my-store/categories/${editingCategory.id}`, {
        name: formData.name,
        slug: generateSlug(formData.name)
      })

      toast.success('Categoría actualizada exitosamente')
      setEditingCategory(null)
      resetForm()
      loadCategories()
    } catch (error) {
      console.error('Error updating category:', error)
      toast.error('Error al actualizar la categoría')
    }
  }

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar esta categoría? Los productos en esta categoría no se eliminarán.')) return

    try {
      await api.delete(`/products/my-store/categories/${categoryId}`)
      toast.success('Categoría eliminada exitosamente')
      loadCategories()
    } catch (error) {
      console.error('Error deleting category:', error)
      toast.error('Error al eliminar la categoría')
    }
  }

  const openEditModal = (category: Category) => {
    setEditingCategory(category)
    setFormData({ name: category.name })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Cargando categorías...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      {/* Header - Modern iMac Style */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-2xl border-b border-gray-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="p-2.5 -ml-2.5 rounded-xl hover:bg-gray-100/60 transition-all duration-200 active:scale-95"
            >
              <ChevronLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex-1 text-center px-4">
              <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
                Gestión de Categorías
              </h1>
              <p className="text-sm text-gray-500 mt-1 font-medium">
                {categories.length} {categories.length === 1 ? 'categoría' : 'categorías'} creada{categories.length === 1 ? '' : 's'}
              </p>
            </div>
            <button
              onClick={() => {
                if (!requireApprovedStore('crear categorías')) return
                setShowCreateModal(true)
              }}
              disabled={!isStoreApproved}
              className={`px-4 py-2.5 rounded-xl font-medium transition-all duration-200 active:scale-95 flex items-center gap-2 ${
                isStoreApproved
                  ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Nueva</span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 safe-area-bottom">
        {/* Mensaje de estado del store */}
        {getStoreStatusMessage() && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 flex items-start gap-3 mb-6 shadow-sm">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-yellow-800 font-medium">{getStoreStatusMessage()}</p>
          </div>
        )}

        {/* Categories List */}
        {categories.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-xl sm:rounded-2xl bg-gray-100 mb-6">
              <Tag className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">No hay categorías</h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">Organiza tus productos creando categorías personalizadas</p>
            <button
              onClick={() => {
                if (!requireApprovedStore('crear categorías')) return
                setShowCreateModal(true)
              }}
              disabled={!isStoreApproved}
              className={`inline-flex items-center px-6 py-3 rounded-xl transition-all duration-200 active:scale-95 font-medium ${
                isStoreApproved
                  ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Plus className="w-5 h-5 mr-2" />
              Crear Primera Categoría
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((category) => (
              <div 
                key={category.id} 
                className="group bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200/80 p-5 hover:shadow-xl hover:shadow-gray-900/5 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0 border border-primary-200/50">
                      <Tag className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{category.name}</h3>
                      <p className="text-sm text-gray-500 font-mono truncate">{category.slug}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-3">
                    <button
                      onClick={() => {
                        if (!requireApprovedStore('editar categorías')) return
                        openEditModal(category)
                      }}
                      disabled={!isStoreApproved}
                      className={`p-2.5 rounded-xl transition-all duration-200 active:scale-95 border ${
                        isStoreApproved
                          ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-100'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                      }`}
                      title="Editar categoría"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (!requireApprovedStore('eliminar categorías')) return
                        handleDeleteCategory(category.id)
                      }}
                      disabled={!isStoreApproved}
                      className={`p-2.5 rounded-xl transition-all duration-200 active:scale-95 border ${
                        isStoreApproved
                          ? 'bg-red-50 text-red-600 hover:bg-red-100 border-red-100'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                      }`}
                      title="Eliminar categoría"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Category Modal */}
      {(showCreateModal || editingCategory) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full border border-gray-200/50">
            <div className="p-8">
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-1">
                  {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
                </h2>
                  <p className="text-sm text-gray-500">
                    {editingCategory ? 'Actualiza la información de la categoría' : 'Crea una nueva categoría para organizar tus productos'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    setEditingCategory(null)
                    resetForm()
                  }}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={editingCategory ? handleEditCategory : handleCreateCategory} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">
                    Nombre de la Categoría *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 focus:bg-white transition-all duration-200 text-gray-900 placeholder-gray-400 shadow-sm hover:shadow-md"
                    placeholder="Ej: Lácteos, Panadería, Bebidas..."
                  />
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-4 border border-blue-200/50">
                  <p className="text-sm font-semibold text-blue-900 mb-1">
                    Slug generado automáticamente:
                  </p>
                  <p className="text-sm font-mono text-blue-700 bg-white/60 px-3 py-2 rounded-lg border border-blue-200/50">
                    {generateSlug(formData.name) || 'Ingresa un nombre para ver el slug'}
                  </p>
                  <p className="text-xs text-blue-600 mt-2 font-medium">
                    El slug se usa para URLs y debe ser único. Se genera automáticamente a partir del nombre.
                  </p>
                </div>

                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false)
                      setEditingCategory(null)
                      resetForm()
                    }}
                    className="flex-1 py-3.5 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 active:scale-95 font-medium border border-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3.5 px-4 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-all duration-200 active:scale-95 font-semibold shadow-md shadow-primary-600/20 hover:shadow-lg hover:shadow-primary-600/30"
                  >
                    {editingCategory ? 'Actualizar' : 'Crear'} Categoría
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StoreCategories
